import React, { useMemo, useState } from 'react';
import { useAppContext } from '../context/AppContextCompat';
import { motion } from 'framer-motion';
import { 
  User, 
  Mail, 
  Phone, 
  MapPin, 
  Building2,
  FileText,
  DollarSign,
  CheckCircle,
  AlertCircle,
  Save
} from 'lucide-react';
import { CraftsmanService } from '../services/api/craftsmanService';
import { useNavigate } from 'react-router-dom';
import { CRAFTSMAN_SPECIALTIES } from '../constants/craftsman';
import { isNativeUi } from '../utils/nativeUi';
import { useMediaQuery } from '../hooks/useMediaQuery';

const CraftsmanSetupPage: React.FC = () => {
  const { currentUser } = useAppContext();
  const navigate = useNavigate();
  const isPhoneWidth = useMediaQuery('(max-width: 767px)');
  const appStyleUi = useMemo(() => isNativeUi(), []) || isPhoneWidth;
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    companyName: '',
    taxId: '',
    description: '',
    phone: currentUser?.phone || '',
    address: '',
    specialties: [] as string[],
    serviceAreas: [] as string[],
    hourlyRate: 800,
    certificates: [] as string[],
    bankAccount: {
      bankCode: '',
      accountNumber: '',
      accountName: ''
    }
  });

  const specialtyOptions = CRAFTSMAN_SPECIALTIES;

  const areaOptions = [
    '台北市',
    '新北市',
    '桃園市',
    '台中市',
    '台南市',
    '高雄市',
    '基隆市',
    '新竹市',
    '新竹縣',
    '苗栗縣',
    '彰化縣',
    '南投縣',
    '雲林縣',
    '嘉義市',
    '嘉義縣',
    '屏東縣',
    '宜蘭縣',
    '花蓮縣',
    '台東縣',
    '澎湖縣',
    '金門縣',
    '連江縣'
  ];

  const handleSpecialtyChange = (specialtyId: string) => {
    setFormData(prev => ({
      ...prev,
      specialties: prev.specialties.includes(specialtyId)
        ? prev.specialties.filter(id => id !== specialtyId)
        : [...prev.specialties, specialtyId]
    }));
  };

  const handleAreaChange = (area: string) => {
    setFormData(prev => ({
      ...prev,
      serviceAreas: prev.serviceAreas.includes(area)
        ? prev.serviceAreas.filter(a => a !== area)
        : [...prev.serviceAreas, area]
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!currentUser) {
      setError('用戶未登入');
      return;
    }

    // 验证必填字段
    if (!formData.companyName || !formData.description) {
      setError('請填寫所有必填欄位');
      return;
    }

    if (formData.specialties.length === 0) {
      setError('請至少選擇一個專業技能');
      return;
    }

    if (formData.serviceAreas.length === 0) {
      setError('請至少選擇一個服務區域');
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const result = await CraftsmanService.createCraftsman({
        userId: currentUser.id,
        companyName: formData.companyName,
        taxId: formData.taxId || null,
        description: formData.description,
        phone: formData.phone,
        address: formData.address,
        specialties: formData.specialties.map(id => parseInt(id)),
        serviceAreas: formData.serviceAreas,
        hourlyRate: formData.hourlyRate,
        certificates: formData.certificates,
        bankAccount: formData.bankAccount
      });

      if (result.success) {
        // 创建成功，跳转到仪表板
        navigate('/craftsman/dashboard');
      } else {
        setError(result.error || '創建工匠資料失敗');
      }
    } catch (error) {
      console.error('Error creating craftsman profile:', error);
      setError('創建工匠資料時發生錯誤');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={`fixnow-app-page fixnow-page-setup min-h-screen bg-gray-50 py-8 ${appStyleUi ? 'fixnow-setup-app-style' : ''}`}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="fixnow-setup-card bg-white rounded-lg shadow-lg"
        >
          {/* 页面标题 */}
          <div className="fixnow-setup-header px-6 py-4 border-b border-gray-200">
            <div className="flex items-center">
              <Building2 className="h-8 w-8 text-blue-600 mr-3" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">設置工匠資料</h1>
                <p className="text-gray-600">請填寫以下資料以完成工匠註冊</p>
              </div>
            </div>
          </div>

          {/* 表单 */}
          <form onSubmit={handleSubmit} className="fixnow-setup-form p-6 space-y-8">
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center">
                  <AlertCircle className="w-5 h-5 text-red-400 mr-2" />
                  <p className="text-red-800">{error}</p>
                </div>
              </div>
            )}

            {/* 基本信息 */}
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-900 border-b border-gray-200 pb-2">
                基本信息
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    工作室名稱 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.companyName}
                    onChange={(e) => setFormData({...formData, companyName: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="例如：王師傅水電行"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    統一編號
                  </label>
                  <input
                    type="text"
                    value={formData.taxId}
                    onChange={(e) => {
                      const numericValue = e.target.value.replace(/\D/g, '');
                      setFormData({...formData, taxId: numericValue});
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="8位數字（選填）"
                    maxLength={8}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    時薪 (NT$) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    required
                    min="0"
                    value={formData.hourlyRate}
                    onChange={(e) => setFormData({...formData, hourlyRate: parseInt(e.target.value)})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="800"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    電話
                  </label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="0912345678"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  地址
                </label>
                <input
                  type="text"
                  value={formData.address}
                  onChange={(e) => setFormData({...formData, address: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="台北市信義區信義路五段7號"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  服務描述 <span className="text-red-500">*</span>
                </label>
                <textarea
                  required
                  rows={4}
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="請描述您的專業技能和服務特色..."
                />
              </div>
            </div>

            {/* 专业技能 */}
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-900 border-b border-gray-200 pb-2">
                專業技能 <span className="text-red-500">*</span>
              </h3>
              
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {specialtyOptions.map((specialty) => (
                  <label key={specialty.id} className="flex items-center space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.specialties.includes(specialty.id)}
                      onChange={() => handleSpecialtyChange(specialty.id)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <span className="text-sm text-gray-700">{specialty.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* 服务区域 */}
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-900 border-b border-gray-200 pb-2">
                服務區域 <span className="text-red-500">*</span>
              </h3>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {areaOptions.map((area) => (
                  <label key={area} className="flex items-center space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.serviceAreas.includes(area)}
                      onChange={() => handleAreaChange(area)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <span className="text-sm text-gray-700">{area}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* 银行账户信息 */}
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-900 border-b border-gray-200 pb-2">
                收款資訊
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    銀行代碼
                  </label>
                  <input
                    type="text"
                    value={formData.bankAccount.bankCode}
                    onChange={(e) => setFormData({
                      ...formData, 
                      bankAccount: {...formData.bankAccount, bankCode: e.target.value}
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="004"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    帳號
                  </label>
                  <input
                    type="text"
                    value={formData.bankAccount.accountNumber}
                    onChange={(e) => setFormData({
                      ...formData, 
                      bankAccount: {...formData.bankAccount, accountNumber: e.target.value}
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="1234567890"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    戶名
                  </label>
                  <input
                    type="text"
                    value={formData.bankAccount.accountName}
                    onChange={(e) => setFormData({
                      ...formData, 
                      bankAccount: {...formData.bankAccount, accountName: e.target.value}
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="王小明"
                  />
                </div>
              </div>
            </div>

            {/* 提交按钮 */}
            <div className="fixnow-setup-actions flex justify-end space-x-4 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={() => navigate('/craftsman/dashboard')}
                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                稍後設置
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="flex items-center px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {isLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    設置中...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    完成設置
                  </>
                )}
              </button>
            </div>
          </form>
        </motion.div>
      </div>
    </div>
  );
};

export default CraftsmanSetupPage;
